import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { appelApi } from './api';
import type { StatutSignature } from './useSignatures';

// ---------------------------------------------------------------------------
// Accompagnement personnalisé (CDC §6.8) : fiche d'entretien sur le modèle
// client, actions suivies, convocations à l'examen. Les règles — retard,
// clôture par la signature, convocation complète — sont tenues par l'API.
// ---------------------------------------------------------------------------

export type StatutAction = 'A_FAIRE' | 'EN_COURS' | 'REALISEE' | 'ABANDONNEE';

export const LIBELLES_STATUT_ACTION: Record<StatutAction, string> = {
  A_FAIRE: 'À faire',
  EN_COURS: 'En cours',
  REALISEE: 'Réalisée',
  ABANDONNEE: 'Abandonnée',
};

/** Rubriques du modèle client « Fiche de suivi d'entretien ». */
export interface FicheAccompagnement {
  objectif: {
    suiviRegulier: boolean;
    pointSituation: boolean;
    difficulteRencontree: boolean;
    projetProfessionnel: boolean;
    autre: string;
  };
  difficultesRencontrees: string;
  pointsPositifs: string;
  demarches: {
    rechercheStageFormation: boolean;
    candidaturesEnvoyees: boolean;
    prisesContact: boolean;
    rendezVous: boolean;
    autre: string;
    detail: string;
  };
  difficultesIdentifiees: {
    motivation: boolean;
    confiance: boolean;
    organisation: boolean;
    communication: boolean;
    problemesPersonnels: boolean;
    autre: string;
  };
  solutionsProposees: string;
  accompagnement: { aideCv: boolean; miseEnRelation: string; autre: string };
  prochainRendezVous: { date: string; objectif: string };
}

export const FICHE_VIERGE: FicheAccompagnement = {
  objectif: { suiviRegulier: false, pointSituation: false, difficulteRencontree: false, projetProfessionnel: false, autre: '' },
  difficultesRencontrees: '',
  pointsPositifs: '',
  demarches: { rechercheStageFormation: false, candidaturesEnvoyees: false, prisesContact: false, rendezVous: false, autre: '', detail: '' },
  difficultesIdentifiees: { motivation: false, confiance: false, organisation: false, communication: false, problemesPersonnels: false, autre: '' },
  solutionsProposees: '',
  accompagnement: { aideCv: false, miseEnRelation: '', autre: '' },
  prochainRendezVous: { date: '', objectif: '' },
};

export interface FicheSignature {
  documentId: string;
  demandeSignatureId: string | null;
  statutSignature: StatutSignature | null;
  signataires: { id: string; nom: string; roleLibelle: string; signeLe: string | null }[];
  empreinteDocument: string | null;
}

export interface LigneAccompagnement {
  id: string;
  apprenantId: string;
  apprenant: string;
  siteCode: string;
  formation: string | null;
  dateEntretien: string;
  actions: { libelle: string; statut: StatutAction }[];
  compteRendu: string | null;
  referent: string | null;
  fiche: FicheSignature | null;
  statut: 'EN_COURS' | 'A_SIGNER' | 'CLOTURE';
  statutLibelle: string;
  statutTon: string;
  clotureLe: string | null;
}

export interface LigneAction {
  id: string;
  suiviId: string;
  apprenantId: string;
  apprenant: string;
  siteCode: string;
  libelle: string;
  echeance: string | null;
  responsable: string | null;
  statut: StatutAction;
  statutLibelle: string;
  enRetard: boolean;
  realiseeLe: string | null;
  commentaire: string | null;
  dateEntretien: string;
}

export interface LigneConvocationExamen {
  cle: string;
  formation: string | null;
  formationCode: string | null;
  siteCode: string;
  dateExamen: string;
  effectif: number;
  convoques: number;
  statut: 'ENVOYEES' | 'PARTIELLES' | 'A_ENVOYER';
  statutLibelle: string;
  statutTon: string;
}

export interface ReponseAccompagnement {
  suivis: LigneAccompagnement[];
  actions: LigneAction[];
  convocations: LigneConvocationExamen[];
  compteurs: {
    suivisEnCours: number;
    suivisClotures: number;
    apprenantsSuivis: number;
    actionsOuvertes: number;
    actionsEnRetard: number;
    convocationsAEnvoyer: number;
  };
}

export interface EntreeSuivi {
  apprenantId: string;
  dateEntretien: string;
  fiche: FicheAccompagnement;
  actions: { libelle: string; echeance?: string; responsable?: string }[];
  compteRendu?: string;
}

function parametresSite(site: string): string {
  return site && site !== 'TOUS' ? `?site=${encodeURIComponent(site)}` : '';
}

function invalider(client: ReturnType<typeof useQueryClient>): void {
  void client.invalidateQueries({ queryKey: ['accompagnements'] });
  void client.invalidateQueries({ queryKey: ['frise-dossier'] });
}

export function useAccompagnements(site: string) {
  const parametres = parametresSite(site);
  return useQuery({
    queryKey: ['accompagnements', parametres],
    queryFn: () => appelApi<ReponseAccompagnement>(`/pedagogique/accompagnement${parametres}`),
  });
}

/** Entretien d'accompagnement : la fiche est produite et mise à la signature. */
export function useCreerSuiviAccompagnement() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ apprenantId, ...corps }: EntreeSuivi) =>
      appelApi<{ id: string; message: string }>(`/pedagogique/accompagnement/${apprenantId}`, {
        methode: 'POST',
        corps,
      }),
    onSuccess: () => invalider(client),
  });
}

export function useModifierAction() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...corps }: { id: string; statut: StatutAction; commentaire?: string }) =>
      appelApi<{ message: string }>(`/pedagogique/accompagnement/actions/${id}`, { methode: 'PATCH', corps }),
    onSuccess: () => invalider(client),
  });
}

export function useConvoquerExamen() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (corps: {
      formationCode: string | null;
      siteCode: string;
      dateExamen: string;
      heure: string;
      lieu: string;
    }) =>
      appelApi<{ convoques: number; employeurs: number; sansAdresse: string[]; message: string }>(
        '/pedagogique/accompagnement/convocations-examen',
        { methode: 'POST', corps },
      ),
    onSuccess: () => invalider(client),
  });
}

/** Fiche ou convocation, lue par une route gardée et tracée. */
export function urlDocumentAccompagnement(documentId: string): string {
  const base = import.meta.env.VITE_API_BASE ?? '/api';
  return `${base}/pedagogique/accompagnement/documents/${documentId}?inline=1`;
}
